import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  DataTableRequest,
  DataTableResponse,
} from '@components/alternative-data-table/alternative-data-table.component';
import {
  NotificationTemplate,
} from '@modules/notifications/notification-template/entities/notification-template.model';
import {
  LIST_NOTIFICATION_TEMPLATES_QUERY,
  NOTIFICATION_TEMPLATE_BY_UUID_QUERY,
  UPDATE_NOTIFICATION_TEMPLATE_MUTATION,
  PREVIEW_NOTIFICATION_TEMPLATE_DOCUMENT_QUERY,
  DOWNLOAD_NOTIFICATION_TEMPLATE_DOCUMENT_QUERY,
} from '@modules/notifications/notification-template/entities/notification-template.graphql';

import { ApolloNamespace } from '@shared/enums/apollo-namespaces';

@Injectable({ providedIn: 'root' })
export class NotificationTemplateApiService {
  constructor(private readonly apollo: Apollo) {}

  /** Lazily resolve the named client when used, not during construction */
  private get client() {
    return this.apollo.use(ApolloNamespace.notifications);
  }

  /** Used by AlternativeDataTable dataProvider */
  getNotificationTemplates(
    req: DataTableRequest
  ): Observable<DataTableResponse<NotificationTemplate>> {
    const pageableParam: any = {
      first: req.page - 1,
      size: req.pageSize,
    };

    if (req.sortBy) {
      pageableParam.sortBy = req.sortBy;
      pageableParam.sortDirection = (req.sortOrder ?? 'asc').toUpperCase();
    }

    if (req.search && req.searchColumns?.length) {
      pageableParam.searchFields = req.searchColumns.map((fieldName) => ({
        fieldName,
        fieldValue: req.search,
        searchType: 'Like',
        queryOperator: 'OR',
        groupId: 1,
      }));
    }

    return this.client
      .query<any>({
        query: LIST_NOTIFICATION_TEMPLATES_QUERY,
        variables: { pageableParam },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          const page = result.data.listNotificationTemplates;
          return {
            items: page.content as NotificationTemplate[],
            total: page.totalElements as number,
          };
        })
      );
  }

  getNotificationTemplatesPage(pageableParam: any): Observable<{
    content: NotificationTemplate[];
    totalElements: number;
    number: number;
    size: number;
  }> {
    return this.client
      .query<any>({
        query: LIST_NOTIFICATION_TEMPLATES_QUERY,
        variables: { pageableParam },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data.listNotificationTemplates)
      );
  }

  /** Used for edit page */
  getNotificationTemplateByUuid(uuid: string): Observable<NotificationTemplate> {
    return this.client
      .query<any>({
        query: NOTIFICATION_TEMPLATE_BY_UUID_QUERY,
        variables: { uuid },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (res) =>
            res.data.notificationTemplateByUuid.data as NotificationTemplate
        )
      );
  }

  /** Update */
  updateNotificationTemplate(
    uuid: string,
    changes: {
      content?: string;
      displayName?: string;
      fileDto?: {
        filename: string;
        description?: string;
        extension?: string;
        data: string;
      };
    }
  ): Observable<NotificationTemplate> {
    const input: any = {
      uuid,
      content: changes.content,
      displayName: changes.displayName,
    };

    if (changes.fileDto) {
      input.fileDto = changes.fileDto;
    }

    return this.client
      .mutate<any>({
        mutation: UPDATE_NOTIFICATION_TEMPLATE_MUTATION,
        variables: { input },
      })
      .pipe(
        map(
          (res) =>
            res.data.updateNotificationTemplate.data as NotificationTemplate
        )
      );
  }

  /** Preview existing document as PDF */
  previewNotificationTemplateDocument(uuid: string): Observable<string> {
    return this.client
      .query<any>({
        query: PREVIEW_NOTIFICATION_TEMPLATE_DOCUMENT_QUERY,
        variables: { uuid },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((res) => res.data.previewNotificationTemplateDocument.data as string)
      );
  }

  /** Download document template as Base64 Word document (.docx) */
  downloadNotificationTemplateDocument(uuid: string): Observable<string> {
    return this.client
      .query<any>({
        query: DOWNLOAD_NOTIFICATION_TEMPLATE_DOCUMENT_QUERY,
        variables: { uuid },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((res) => res.data.downloadNotificationTemplateDocument.data as string)
      );
  }
}
